import ThemedSelect from "../shared/ThemedSelect.jsx";
import { useEffect, useRef, useState } from "react";
import { Badge, Button, Form, Table } from "react-bootstrap";
import {
  listVariations,
  createVariation,
  updateVariation,
  deleteVariation,
  listAdditions,
  createAddition,
  updateAddition,
  deleteAddition,
  listEntityRows,
  reorderList,
  getQuoteFees,
  resetVariationFee,
} from "../../services/apiClient.js";
import { formatDate, toInputDate, todayInput } from "../../utils/date.js";
import PricedLineGrid from "./PricedLineGrid.jsx";
import FeeProfileLine from "./FeeProfileLine.jsx";
import { SortableRows, SortableTr } from "../SortableRows.jsx";

// Variations: a headers grid (date / description / reason / approved) over the
// shared priced grid for the costing lines, where each line picks the Variation it
// belongs to. (Legacy GPSVariation + GPSEstimateAddition.)
//
// Since 2026-09-05 each header also grows the quote's four automatic fee lines
// from its own costing lines (quotes.md 8.29). The STATES live on the header
// row (pg_line_state …), so the grid is handed a per-row resolver instead of
// the claim-level status the quote grid uses; the claim-level status is still
// read here for the ENABLED flags (which fees this job's profile charges).

const ADDITION_API = {
  list: listAdditions,
  create: createAddition,
  update: updateAddition,
  remove: deleteAddition,
  reorder: (claimId, ids) => reorderList(claimId, "estimate-additions", ids),
};

const FEE_KINDS = ["pg", "jmf", "profit", "rubbish"];
const FEE_LABEL = { pg: "P&G", jmf: "JMF", profit: "Profit", rubbish: "Rubbish" };
const stateOf = (v, kind) => v?.[`${kind}_line_state`];

const BLANK = { variation_date: "", description: "", reason_id: "", is_approved: false };

function variationLabel(v) {
  const desc = (v.description || "").trim();
  return `#${v.line_no}${desc ? ` — ${desc.slice(0, 40)}` : ""}`;
}

function VariationsSection({ claimId }) {
  const [variations, setVariations] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [editingId, setEditingId] = useState(null); // null | "new" | id
  const [form, setForm] = useState(BLANK);
  const [error, setError] = useState("");
  const [focusTick, setFocusTick] = useState(0);
  const firstRef = useRef(null);
  const [feeStatus, setFeeStatus] = useState(null);
  const [gridReload, setGridReload] = useState(0);
  // How many costing rows the grid holds — the pricing-profile line above it
  // shows only once there are entries (Robert 2026-09-05), unlike the quote
  // grid's, whose empty-state wording refers to it.
  const [costingCount, setCostingCount] = useState(0);

  function loadVariations() {
    return listVariations(claimId)
      .then(setVariations)
      .catch(() => setError("Unable to load variations."));
  }
  useEffect(() => {
    loadVariations();
    listEntityRows("variation-reasons").then(setReasons).catch(() => {});
    getQuoteFees(claimId).then(setFeeStatus).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId]);

  useEffect(() => {
    if (editingId) firstRef.current?.focus();
  }, [focusTick, editingId]);

  const set = (patch) => setForm((c) => ({ ...c, ...patch }));

  function startAdd() {
    setError("");
    // A new header is dated today (NZ's today — todayInput, never a UTC
    // slice); it stays editable for a back-dated one (Robert 2026-09-05).
    setForm({ ...BLANK, variation_date: todayInput() });
    setEditingId("new");
    setFocusTick((t) => t + 1);
  }
  function startEdit(v) {
    setError("");
    setForm({
      variation_date: toInputDate(v.variation_date) || "",
      description: v.description || "",
      reason_id: v.reason_id || "",
      is_approved: !!v.is_approved,
    });
    setEditingId(v.id);
    setFocusTick((t) => t + 1);
  }
  async function save() {
    setError("");
    try {
      if (editingId === "new") {
        await createVariation(claimId, form);
        await loadVariations();
        // One header at a time: unlike costing lines, headers do not come in
        // batches, so the editor closes rather than reopening blank (Robert
        // 2026-09-05). The next step is costing it, and the costings entry
        // row already defaults to the newest header.
        setForm(BLANK);
        setEditingId(null);
      } else {
        await updateVariation(claimId, editingId, form);
        await loadVariations();
        setEditingId(null);
      }
    } catch (err) {
      setError(err.response?.data?.error || "Unable to save variation.");
    }
  }
  async function remove(v) {
    if (!window.confirm(`Delete variation #${v.line_no}? Its costing lines stay but become unassigned.`)) return;
    setError("");
    try {
      await deleteVariation(claimId, v.id);
      await loadVariations();
      setGridReload((n) => n + 1); // its auto fee lines went with it
    } catch (err) {
      setError(err.response?.data?.error || "Unable to delete variation.");
    }
  }

  // Drag-and-drop: headers renumber (#1, #2, …) on the server, so reload after
  // saving — the costing-line "Variation" labels follow the new numbers.
  const dragDisabled = editingId !== null;
  async function handleReorder(reordered) {
    const prev = variations;
    setVariations(reordered);
    try {
      await reorderList(claimId, "variations", reordered.map((v) => v.id));
      await loadVariations();
    } catch (err) {
      setVariations(prev);
      setError(err.response?.data?.error || "Unable to save the new order.");
    }
  }

  // --- per-header auto fees -------------------------------------------------
  const feeEnabled = (kind) => !!feeStatus?.[kind]?.enabled;
  // One endpoint serves Reset / Restore / Enable; reloadGrid is false when the
  // grid is about to reload itself (its own Reset button does).
  async function resetFee(v, kind, reloadGrid = true) {
    setError("");
    try {
      await resetVariationFee(claimId, v.id, kind);
      await loadVariations();
      if (reloadGrid) setGridReload((n) => n + 1);
    } catch (err) {
      setError(err.response?.data?.error || "Unable to reset the fee line.");
    }
  }
  const headerOf = (row) => variations.find((v) => Number(v.id) === Number(row.variation_id)) || null;
  const rowFeeState = (row) => stateOf(headerOf(row), row.line_kind) || null;
  const onFeeReset = (kind, row) => {
    const v = headerOf(row);
    return v ? resetFee(v, kind, false) : Promise.resolve();
  };

  // Enable = every disabled-but-charged kind to auto, in one click (the quote
  // toolbar's "Enable automatic fees", per header). Nothing old is touched.
  const enableKinds = (v) => FEE_KINDS.filter((k) => stateOf(v, k) === "disabled" && feeEnabled(k));
  const restoreKinds = (v) => FEE_KINDS.filter((k) => stateOf(v, k) === "removed" && feeEnabled(k));
  async function enableFees(v) {
    for (const kind of enableKinds(v)) {
      // eslint-disable-next-line no-await-in-loop
      await resetFee(v, kind, false);
    }
    setGridReload((n) => n + 1);
  }

  function feeControls(v) {
    const enable = enableKinds(v);
    const restore = restoreKinds(v);
    if (enable.length === 0 && restore.length === 0) return null;
    return (
      <div className="mt-1 d-flex flex-wrap gap-1">
        {enable.length > 0 ? (
          <Button
            size="sm"
            variant="outline-success"
            className="py-0"
            data-hint="Turn on the automatic fee lines for this variation — its existing costing lines are left untouched"
            onClick={() => enableFees(v)}
          >
            Enable automatic fees
          </Button>
        ) : null}
        {restore.map((kind) => (
          <Button
            key={kind}
            size="sm"
            variant="outline-secondary"
            className="py-0"
            data-hint="This auto line was deleted — put it back and recalculate"
            onClick={() => resetFee(v, kind)}
          >
            Restore {FEE_LABEL[kind]}
          </Button>
        ))}
      </div>
    );
  }

  function editRow(key) {
    return (
      <tr key={key}>
        <td />
        <td className="text-muted">{key === "new" ? "New" : ""}</td>
        <td>
          <Form.Control
            size="sm"
            type="date"
            value={form.variation_date}
            onChange={(e) => set({ variation_date: e.target.value })}
          />
        </td>
        <td>
          <Form.Control
            as="textarea"
            rows={2}
            size="sm"
            ref={firstRef}
            value={form.description}
            onChange={(e) => set({ description: e.target.value })}
          />
        </td>
        <td>
          <ThemedSelect size="sm" value={form.reason_id} onChange={(e) => set({ reason_id: e.target.value })}>
            <option value="">— None —</option>
            {reasons.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </ThemedSelect>
        </td>
        <td className="text-center">
          <Form.Check
            type="checkbox"
            checked={form.is_approved}
            onChange={(e) => set({ is_approved: e.target.checked })}
          />
        </td>
        <td className="text-end text-nowrap">
          <Button size="sm" className="me-2" onClick={save}>{key === "new" ? "Add" : "Save"}</Button>
          <Button size="sm" variant="secondary" onClick={() => setEditingId(null)}>
            {key === "new" ? "Done" : "Cancel"}
          </Button>
        </td>
      </tr>
    );
  }

  function viewRow(v) {
    return (
      <SortableTr key={v.id} id={v.id} disabled={dragDisabled}>
        <td className="text-muted">{v.line_no}</td>
        <td className="text-nowrap">{v.variation_date ? formatDate(v.variation_date) : "—"}</td>
        <td>
          <div style={{ whiteSpace: "pre-wrap" }}>{v.description || <span className="text-muted">—</span>}</div>
          {feeControls(v)}
        </td>
        <td>{v.reason_name || <span className="text-muted">—</span>}</td>
        <td className="text-center">
          {v.is_approved ? <Badge bg="success">Approved</Badge> : <span className="text-muted">No</span>}
        </td>
        <td className="text-end text-nowrap">
          <Button variant="outline-primary" size="sm" className="me-2" onClick={() => startEdit(v)}>Edit</Button>
          <Button variant="outline-danger" size="sm" onClick={() => remove(v)}>Delete</Button>
        </td>
      </SortableTr>
    );
  }

  const owner = {
    field: "variation_id",
    label: "Variation",
    options: variations.map((v) => ({ id: v.id, name: variationLabel(v) })),
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">Variations</h5>
        {editingId === null ? (
          <Button size="sm" variant="success" onClick={startAdd}>+ Add Variation</Button>
        ) : null}
      </div>

      {error ? <div className="alert alert-danger">{error}</div> : null}

      <SortableRows items={variations} onReorder={handleReorder} disabled={dragDisabled}>
      <Table striped bordered hover responsive size="sm" className="mb-3 align-middle">
        <thead className="table-surface">
          <tr>
            <th style={{ width: 36 }} />
            <th style={{ width: 34 }}>#</th>
            <th style={{ width: 130 }}>Date</th>
            <th>Description</th>
            <th style={{ width: 200 }}>Reason</th>
            <th style={{ width: 90 }} className="text-center">Approved</th>
            <th style={{ width: 160 }} className="text-end">Actions</th>
          </tr>
        </thead>
        <tbody>
          {variations.map((v) => (editingId === v.id ? editRow(v.id) : viewRow(v)))}
          {editingId === "new" ? editRow("new") : null}
          {variations.length === 0 && editingId !== "new" ? (
            <tr>
              <td colSpan={7} className="text-center text-muted">No variations.</td>
            </tr>
          ) : null}
        </tbody>
      </Table>
      </SortableRows>

      {/* The same rates drive the costings' automatic lines as the quote's,
          so the same pricing-profile line sits above this grid too — once it
          has entries. No claim-level states here: a disabled quote never
          disables a new header (quotes.md 8.29). */}
      {costingCount > 0 ? <FeeProfileLine feeStatus={feeStatus} /> : null}

      {/* Same required-fields language as the estimates grid (KR 2026-08-20):
          red stars on Work Centre + Quantity and the refusal shown IN the
          editor row — costings feed progress claims the same way. The fee
          badges and Reset come from the grid; the state is the HEADER's. */}
      <PricedLineGrid
        claimId={claimId}
        api={ADDITION_API}
        title="Variation Costings"
        owner={owner}
        requireWorkCentreQty
        rowFeeState={rowFeeState}
        onFeeReset={onFeeReset}
        onLinesChanged={(rows) => setCostingCount(rows.length)}
        reloadKey={gridReload}
      />
    </>
  );
}

export default VariationsSection;
